import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Scale, 
  ShieldCheck, 
  Briefcase, 
  Users, 
  Check, 
  Calendar, 
  Clock,
  Phone,
  PhoneCall, 
  Mail, 
  MapPin, 
  ArrowRight, 
  Star, 
  ChevronRight,
  ChevronDown,
  Sparkles,
  Award,
  Building2,
  FileText,
  ExternalLink,
  Loader2,
  MessageSquare,
  Lock
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import BrandLogo from '../../components/common/BrandLogo';
import IntegratedBookingSection from '../../components/booking/IntegratedBookingSection';
import InstantBookingModal from '../../components/booking/InstantBookingModal';
import PracticeAreaCatalogModal from '../../components/common/PracticeAreaCatalogModal';
import LawyerDetailModal from '../../components/lawyers/LawyerDetailModal';
import ReviewSubmissionModal from '../../components/reviews/ReviewSubmissionModal';
import PlatformGuideSection from '../../components/common/PlatformGuideSection';
import SubscriptionModal from '../../components/subscription/SubscriptionModal';
import ClientChatModal from '../../components/chat/ClientChatModal';
import GoogleMapPreview from '../../components/common/GoogleMapPreview';
import { showToast } from '../../components/common/Toast';
import { formatUzPhone, isCompleteUzPhone, capitalizeWords, localizeOfficeName, localizeWorkingHours, localizeLawyerPosition } from '../../utils/helpers';
import { PRACTICE_TOPICS } from '../../data/practiceTopics';

const INITIAL_CORE_SERVICES = [
  {
    id: 1,
    slug: 'jinoyat-ishlari',
    titleUz: 'Jinoyat ishlari bo‘yicha',
    titleRu: 'По уголовным делам',
    titleEn: 'Criminal Cases',
    shortDescUz: 'Surishtiruv, dastlabki tergov, prokuratura va barcha sud instansiyalarida fuqarolar hamda mansabdor shaxslarni himoya qilish.',
    shortDescRu: 'Дознание, предварительное следствие, прокуратура и защита граждан и должностных лиц во всех судебных инстанциях.',
    shortDescEn: 'Inquiry, preliminary investigation, prosecution, and defense of citizens and officials in all court instances.',
    sortOrder: 1,
  },
  {
    id: 2,
    slug: 'fuqarolik-ishlari',
    titleUz: 'Fuqarolik ishlari bo‘yicha',
    titleRu: 'По гражданским делам',
    titleEn: 'Civil Cases',
    shortDescUz: 'Mulkiy nizolar, shartnoma majburiyatlari, yetkazilgan zararni undirish, uy-joy, meros va oilaviy nizolarni sudda hal etish.',
    shortDescRu: 'Имущественные споры, договорные обязательства, возмещение ущерба, жилищные, наследственные и семейные дела в суде.',
    shortDescEn: 'Property disputes, contractual obligations, damages restitution, housing, inheritance, and civil litigation.',
    sortOrder: 2,
  },
  {
    id: 3,
    slug: 'mamuriy-ishlar',
    titleUz: 'Ma’muriy ishlar bo‘yicha',
    titleRu: 'По административным делам',
    titleEn: 'Administrative Cases',
    shortDescUz: 'Davlat organlari, mansabdor shaxslar va nazorat idoralari qarorlari ustidan shikoyat qilish, ma’muriy huquqbuzarliklar bo‘yicha himoya.',
    shortDescRu: 'Обжалование решений государственных органов и должностных лиц, защита по делам об административных правонарушениях.',
    shortDescEn: 'Appealing decisions of government bodies and officials, defense in administrative offenses and regulatory disputes.',
    sortOrder: 3,
  },
  {
    id: 4,
    slug: 'iqtisodiy-ishlar',
    titleUz: 'Iqtisodiy ishlar bo‘yicha',
    titleRu: 'По экономическим делам',
    titleEn: 'Economic & Commercial Cases',
    shortDescUz: 'Xo‘jalik va biznes nizolari, shartnomaviy qarzlar va peniyalarni undirish, bankrotlik, soliq va korporativ nizolarda vakillik.',
    shortDescRu: 'Хозяйственные и коммерческие споры, взыскание задолженностей и пеней, банкротство, налоговые и корпоративные дела.',
    shortDescEn: 'Commercial and business disputes, recovery of debts and penalties, bankruptcy, tax, and corporate representation.',
    sortOrder: 4,
  },
];

export default function HomePage() {
  const { t, localized, currentLang } = useLanguage();
  const { isSubscribed } = useAuth();

  const [services, setServices] = useState(INITIAL_CORE_SERVICES);
  const [lawyers, setLawyers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedLawyerForDetail, setSelectedLawyerForDetail] = useState(null);
  const [selectedLawyerForBooking, setSelectedLawyerForBooking] = useState(null);
  const [selectedLawyerForReview, setSelectedLawyerForReview] = useState(null);
  const [selectedChatLawyer, setSelectedChatLawyer] = useState(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  // Quick hero consultation form state
  const [quickName, setQuickName] = useState('');
  const [quickEmail, setQuickEmail] = useState('');
  const [quickPhone, setQuickPhone] = useState('+998 ()');
  const [quickLawyerId, setQuickLawyerId] = useState('');
  const [quickDesc, setQuickDesc] = useState('');
  const [quickSubmitting, setQuickSubmitting] = useState(false);

  // Lawyer section slide-in animation state & observer (Image 5)
  const lawyersSectionRef = useRef(null);
  const [lawyersVisible, setLawyersVisible] = useState(true);

  // Multilingual Topic catalog definitions
  const practiceTopics = PRACTICE_TOPICS;

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const catalogParam = searchParams.get('catalog');
    if (catalogParam) {
      const found = PRACTICE_TOPICS.find((t) => t.id === catalogParam);
      if (found) {
        setSelectedTopic(found);
        setCatalogModalOpen(true);
      }
    }
  }, [searchParams]);

  // High-standard default reviews in 3 languages
  const defaultReviews = [
    {
      id: 1,
      clientName: "Akmal Karimov",
      clientTitle: "«Oazis Agro Invest» Bosh direktori",
      rating: 5,
      commentUz: "Sevinch advokatlar hay’ati jamoasi korxonamiz bo‘yicha 4 milliard so‘mlik nohaq da’voni sudda to‘liq bekor qilib berdi. Samimiy tavsiya qilaman!",
      commentRu: "Команда адвокатов Sevinch блестяще выиграла сложный арбитражный спор на 4 миллиарда сумов. Абсолютный профессионализм и безупречная стратегия!",
      commentEn: "The Sevinch Advocates team secured a complete dismissal of a 4-billion UZS claim against our enterprise. World-class trial representation!",
    },
    {
      id: 2,
      clientName: "Nodira Qosimova",
      clientTitle: "Xususiy tadbirkor",
      rating: 5,
      commentUz: "Meros taqsimoti va ko‘chmas mulk nizolarida tezkor, aniq va adolatli natijaga erishdik. Advokatlarning samimiy va professional yondashuvi uchun cheksiz minnatdorman!",
      commentRu: "Помогли восстановить права на имущество и разрешить наследственный спор в кратчайшие сроки. Грамотные и честные юристы!",
      commentEn: "Resolved our complex inheritance and real estate disputes quickly and equitably. Highly diligent and trustworthy attorneys!",
    },
    {
      id: 3,
      clientName: "Rustam Mirzayev",
      clientTitle: "«Golden Bridge Logistics» Ta’sischisi",
      rating: 5,
      commentUz: "Eksport shartnomalarimiz va soliq organlari bilan yuzaga kelgan murakkab bahsda to‘liq g‘alaba qozondik. Jizzaxdagi eng kuchli yuridik amaliyot!",
      commentRu: "Выиграли спор по внешнеэкономическому контракту и защитили от неправомерных налоговых доначислений. Сильнейшая юридическая практика!",
      commentEn: "Successfully resolved supply contract disputes and defended our company against unlawful regulatory sanctions. Truly the top legal team!",
    }
  ];

  const handleBookLawyer = (lawyer) => {
    setSelectedLawyerForBooking(lawyer);
    setBookingModalOpen(true);
  };

  useEffect(() => {
    Promise.all([
      api.get('/services'),
      api.get('/lawyers'),
      api.get('/offices'),
      api.get('/reviews'),
    ])
      .then(([sRes, lRes, oRes, rRes]) => {
        if (sRes?.success) setServices(sRes.services || []);
        if (lRes?.success) {
          const sorted = [...(lRes.lawyers || [])]
            .filter((l) => Boolean(l.isActive) === true)
            .sort((a, b) =>
              (a.fullName || '').localeCompare(b.fullName || '', 'uz', { sensitivity: 'base' })
            );
          setLawyers(sorted);
          if (sorted[0]?.id) {
            setQuickLawyerId(String(sorted[0].id));
          }
        }
        if (oRes?.success) {
          setOffices(oRes.offices || []);
          if (oRes.offices?.[0]) setSelectedOffice(oRes.offices[0]);
        }
        if (rRes?.success) setReviews(rRes.reviews || []);
      })
      .catch((err) => {
        console.error('Error loading homepage data:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Trigger entrance slide-in animation when lawyers section enters viewport
  useEffect(() => {
    if (!lawyersSectionRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLawyersVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(lawyersSectionRef.current);
    return () => observer.disconnect();
  }, [lawyers.length]);

  // Open catalog for a given topic
  const handleOpenTopicCatalog = (topicOrService) => {
    const match = PRACTICE_TOPICS.find(
      (t) =>
        t.id === topicOrService.id ||
        t.titleUz?.toLowerCase() === (topicOrService.titleUz || topicOrService.title)?.toLowerCase() ||
        t.id === topicOrService.slug
    );
    setSelectedTopic(match || topicOrService);
    setCatalogModalOpen(true);
  };

  // When user clicks "Book for this topic" from catalog modal
  const handleBookFromCatalog = (topic) => {
    setSelectedTopic(topic);
    setBookingModalOpen(true);
  };

  // Submit quick consultation form in Hero
  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    if (!quickName.trim() || !isCompleteUzPhone(quickPhone)) {
      showToast(currentLang === 'uz' ? 'Ism va to‘liq telefon raqamingizni kiriting (+998 (97) 325-07-70)' : currentLang === 'ru' ? 'Введите имя и полный номер телефона (+998 (97) 325-07-70)' : 'Please enter your name and full phone number (+998 (97) 325-07-70)', 'error');
      return;
    }
    setQuickSubmitting(true);
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dateStr = tomorrow.toISOString().split('T')[0];

      const payload = {
        clientName: quickName.trim(),
        clientPhone: quickPhone.trim(),
        clientEmail: quickEmail.trim() || 'consultation@jizzaxadvokatlari.uz',
        serviceId: services[0]?.id || 1,
        lawyerId: quickLawyerId ? parseInt(quickLawyerId, 10) : (lawyers[0]?.id || 1),
        officeId: offices[0]?.id || 1,
        appointmentDate: dateStr,
        startTime: '10:00',
        isQuickConsultation: true,
        clientReason: quickDesc.trim() 
          ? `[TEZKOR KONSULTATSIYA] ${quickDesc.trim()}`
          : '[TEZKOR KONSULTATSIYA] Dastlabki yuridik konsultatsiya va hujjatlarni ekspertizadan o‘tkazish bo‘yicha tezkor murojaat.',
      };

      const res = await api.post('/public/appointments', payload);
      if (res?.success) {
        showToast(
          currentLang === 'uz'
            ? `Arizangiz qabul qilindi! Tez orada advokat siz bilan bog‘lanadi. Bron kodi: ${res.bookingReference}`
            : currentLang === 'ru'
            ? `Заявка принята! Адвокат свяжется с вами в ближайшее время. Код брони: ${res.bookingReference}`
            : `Consultation request received! An attorney will contact you soon. Ref: ${res.bookingReference}`,
          'success'
        );
        setQuickName('');
        setQuickEmail('');
        setQuickPhone('+998 ()');
        setQuickDesc('');
      } else {
        showToast(res?.message || 'Xatolik yuz berdi', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Xatolik yuz berdi', 'error');
    } finally {
      setQuickSubmitting(false);
    }
  };

  return (
    <div className="bg-[#05080e] text-slate-100 min-h-screen">
      
      {/* =========================================================================
          1. HERO SECTION (Mirroring Top Half of Reference Image)
      ========================================================================= */}
      <section id="hero" className="relative bg-[#05080e] pt-12 pb-20 lg:pt-16 lg:pb-28 overflow-hidden border-b border-[#141d2d]">
        
        {/* Background Court Office Image with Elegant Dark Luxury Overlay (User Requirement: Image 2) */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <img
            src="/hero_law_office.png"
            alt="Jizzax Advokatlari Fon"
            className="w-full h-full object-cover object-center filter brightness-[0.55] contrast-[1.12]"
          />
          {/* Deep dark gradient overlay — sharp and visible, yet text 100% legible without hurting eyes */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#05080e]/82 via-[#05080e]/68 to-[#05080e]/78" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05080e] via-transparent to-[#05080e]/70" />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#c59b27]/12 blur-[130px] rounded-full" />
          <div className="absolute bottom-0 right-1/4 w-[32rem] h-[32rem] bg-[#14233c]/35 blur-[150px] rounded-full" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* LEFT COLUMN: Hero Copy & Feature Badges (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              
              {/* Spaced Gold Category Label */}
              <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block">
                {t('legal_solutions_tag', 'LEGAL SOLUTIONS')}
              </span>

              {/* Large Serif Headline */}
              <h1 className="font-serif text-[28px] sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-100 leading-[1.16] sm:leading-[1.12] tracking-tight break-words">
                {t('hero_title_p1', 'Dedicated to')}{' '}
                <br className="hidden sm:inline" />
                {t('hero_title_p2', 'Protecting Your')}{' '}
                <br className="hidden sm:inline" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] via-[#f3e5ab] to-[#c59b27]">
                  {t('hero_title_highlight', 'Rights')}
                </span>
              </h1>

              {/* Subheading */}
              <p className="text-slate-300 text-xs sm:text-sm lg:text-base leading-relaxed font-light max-w-lg">
                {t('hero_subheadline', 'We provide strategic legal counsel and aggressive representation to individuals and businesses.')}
              </p>

              {/* Two CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
                {/* Gold Button -> Opens Instant Booking Modal without jumping */}
                <button
                  type="button"
                  onClick={() => setBookingModalOpen(true)}
                  className="w-full sm:w-auto px-5 sm:px-7 py-3.5 rounded bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] text-white text-xs font-bold uppercase tracking-[0.12em] sm:tracking-[0.14em] shadow-lg shadow-[#b38038]/25 transition-all text-center cursor-pointer"
                >
                  {t('hero_schedule_consult', 'SCHEDULE A CONSULTATION')}
                </button>

                {/* Dark Gold Outline Button -> Opens Practice Areas Catalog */}
                <button
                  type="button"
                  onClick={() => handleOpenTopicCatalog(practiceTopics[0])}
                  className="w-full sm:w-auto px-5 sm:px-7 py-3.5 rounded border border-[#c59b27]/40 bg-[#0a0f19]/70 hover:bg-[#121a2b] hover:border-[#c59b27] text-slate-200 text-xs font-bold uppercase tracking-[0.12em] sm:tracking-[0.14em] transition-all text-center cursor-pointer"
                >
                  {t('hero_our_practices', 'OUR PRACTICE AREAS')}
                </button>
              </div>

            </div>

            {/* RIGHT COLUMN: Floating REQUEST A CONSULTATION Card (5 cols) */}
            <div className="lg:col-span-5">
              <div className="relative bg-[#0a0f19]/95 border border-[#c59b27]/40 hover:border-[#c59b27]/80 rounded-2xl p-5 sm:p-7 lg:p-8 shadow-2xl shadow-black/95 backdrop-blur-md overflow-hidden transition-all duration-300 group">
                
                {/* Elegant Ambient Glowing Backlights */}
                <div className="absolute -top-20 -right-20 w-44 h-44 bg-[#c59b27]/15 rounded-full blur-3xl animate-pulse pointer-events-none" />
                <div className="absolute -bottom-20 -left-20 w-44 h-44 bg-emerald-500/10 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1.5s' }} />

                {/* Free consultation banner */}
                <div className="flex items-center justify-between mb-2.5 relative z-10">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#c59b27] font-semibold">
                    {t('quick_consult_firm', 'JIZZAX ADVOKATLARI')}
                  </span>
                  {/* Animated glowing pill */}
                  <span className="relative inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500/15 via-emerald-500/25 to-emerald-500/15 border border-emerald-400/50 text-emerald-300 text-[10px] font-bold tracking-wider uppercase shadow-sm shadow-emerald-500/20 hover:scale-105 transition-transform">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                    </span>
                    <span className="animate-pulse">{t('consultation_free_badge', 'Bizda konsultatsiya bepul')}</span>
                  </span>
                </div>

                <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-100 tracking-tight mb-2 relative z-10">
                  {t('quick_consult_title', 'Tezkor Konsultatsiya')}
                </h3>
                <p className="text-slate-400 text-xs mb-5 font-light leading-relaxed relative z-10">
                  {t('quick_consult_desc', 'Mutaxassis bilan bog‘laning va dastlabki huquqiy yordam oling. Bizda konsultatsiya bepul!')}
                </p>

                <form onSubmit={handleQuickSubmit} className="space-y-3.5 relative z-10">
                  <div>
                    <input
                      type="text"
                      required
                      placeholder={t('input_name', 'Ism va Familiya')}
                      value={quickName}
                      onChange={(e) => setQuickName(capitalizeWords(e.target.value))}
                      className="w-full px-3.5 py-2.5 bg-[#070b13] border border-[#162238] focus:border-[#c59b27] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      required
                      placeholder="+998 (97) 325-07-70"
                      value={quickPhone || '+998 ()'}
                      onFocus={() => {
                        if (!quickPhone || quickPhone === '') setQuickPhone('+998 ()');
                      }}
                      onChange={(e) => setQuickPhone(formatUzPhone(e.target.value, quickPhone))}
                      className="w-full px-3.5 py-2.5 bg-[#070b13] border border-[#162238] focus:border-[#c59b27] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none font-mono transition-colors"
                    />
                  </div>

                  {/* Lawyer Selection Dropdown (User Requirement: Image 1 & 2) */}
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-slate-300 font-semibold mb-1">
                      {t('select_lawyer_label', 'Mas’ul Advokatni tanlang')} <span className="text-[#c59b27]">*</span>
                    </label>
                    <div className="relative">
                      <select
                        required
                        value={quickLawyerId}
                        onChange={(e) => setQuickLawyerId(e.target.value)}
                        className="w-full pl-3.5 pr-8 py-2.5 bg-[#070b13] border border-[#162238] rounded-lg text-xs text-slate-100 focus:outline-none focus:border-[#c59b27] transition-colors appearance-none cursor-pointer"
                      >
                        {lawyers.map((l) => (
                          <option key={l.id} value={l.id} className="bg-[#0b1220] text-slate-200">
                            {l.fullName} ({l.position || l.specialization || 'Advokat'})
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <textarea
                      rows={2}
                      placeholder={t('input_reason_opt', 'Murojaat sababi (qisqacha)...')}
                      value={quickDesc}
                      onChange={(e) => setQuickDesc(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-[#070b13] border border-[#162238] focus:border-[#c59b27] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={quickSubmitting}
                    className="relative overflow-hidden w-full py-3.5 px-4 rounded-lg bg-gradient-to-r from-[#b38038] via-[#c59b27] to-[#d4af37] hover:from-[#c59b27] hover:to-[#e5c158] text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-[#b38038]/30 hover:shadow-[#c59b27]/50 active:scale-[0.99] transition-all cursor-pointer disabled:opacity-50 mt-2 group/btn"
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {quickSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin text-white" />
                          <span>{t('submitting', 'Yuborilmoqda...')}</span>
                        </>
                      ) : (
                        <>
                          <span>{t('btn_submit_request', 'ARIZANI YUBORISH')}</span>
                          <span className="group-hover/btn:translate-x-1 transition-transform">→</span>
                        </>
                      )}
                    </span>
                    {/* Shimmer sweep */}
                    <div className="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 ease-in-out pointer-events-none" />
                  </button>
                </form>

              </div>
            </div>

          </div>

          {/* Centered 3 Benefit Items across the screen (Clean, frameless, static) */}
          <div className="mt-8 sm:mt-10 pt-6 border-t border-[#141d2d]/80 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 text-center">
              
              <div className="flex flex-col items-center justify-center">
                <div className="flex items-center justify-center text-[#c59b27] mb-1">
                  <Scale className="w-4 h-4 mr-1.5 shrink-0" />
                  <span className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-slate-200">
                    {t('badge_experienced', 'EXPERIENCED ATTORNEYS')}
                  </span>
                </div>
                <p className="text-[10px] sm:text-[11px] text-slate-400 font-light leading-relaxed max-w-[260px]">
                  {t('badge_experienced_desc', 'Decades of combined legal experience')}
                </p>
              </div>

              <div className="flex flex-col items-center justify-center">
                <div className="flex items-center justify-center text-[#c59b27] mb-1">
                  <ShieldCheck className="w-4 h-4 mr-1.5 shrink-0" />
                  <span className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-slate-200">
                    {t('badge_results', 'PROVEN RESULTS')}
                  </span>
                </div>
                <p className="text-[10px] sm:text-[11px] text-slate-400 font-light leading-relaxed max-w-[260px]">
                  {t('badge_results_desc', 'Successful outcomes in complex cases')}
                </p>
              </div>

              <div className="flex flex-col items-center justify-center">
                <div className="flex items-center justify-center text-[#c59b27] mb-1">
                  <Users className="w-4 h-4 mr-1.5 shrink-0" />
                  <span className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-slate-200">
                    {t('badge_client', 'CLIENT FOCUSED')}
                  </span>
                </div>
                <p className="text-[10px] sm:text-[11px] text-slate-400 font-light leading-relaxed max-w-[260px]">
                  {t('badge_client_desc', 'Personalized service & trusted guidance')}
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>


      {/* =========================================================================
          2. LUMINOUS GOLD RIBBON / SECTION DIVIDER
      ========================================================================= */}
      <div className="relative py-6 sm:py-8 bg-[#04060a] border-y border-[#141d2d] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center space-x-3 sm:space-x-6">
          {/* Left glowing gradient line */}
          <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-[#c59b27]/60 to-[#c59b27]" />
          
          {/* Center Title */}
          <div className="flex flex-col items-center text-center">
            <span className="font-serif text-xl sm:text-2xl md:text-3xl font-bold tracking-[0.2em] sm:tracking-[0.25em] text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] via-[#f3e5ab] to-[#c59b27] uppercase">
              {t('legal_solutions_tag', 'LEGAL')}
            </span>
            <span className="text-[9px] sm:text-[10px] md:text-xs uppercase tracking-[0.25em] sm:tracking-[0.35em] text-[#c59b27] font-semibold mt-0.5">
              {t('legal_ribbon_title', 'WEBSITE DESIGN & STRATEGIC DEFENSE')}
            </span>
          </div>

          {/* Right glowing gradient line */}
          <div className="h-[1px] flex-1 bg-gradient-to-r from-[#c59b27] via-[#c59b27]/60 to-transparent" />
        </div>
      </div>


      {/* =========================================================================
          3. SECTION: ADVOKATLAR HAQIDA (ABOUT THE ATTORNEYS)
      ========================================================================= */}
      <section id="about" className="relative py-20 sm:py-28 overflow-hidden border-b border-[#141d2d]">
        {/* Luxury Executive Office Backdrop - Enhanced Clarity & Visibility */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-65 filter brightness-[0.70] contrast-[1.12] scale-105 transition-all duration-700"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000')`,
          }}
        />
        {/* Tuned Vignette Overlay: Keeps Text Crisp while Revealing Office Architecture */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#05080e]/95 via-[#05080e]/75 to-[#05080e]/95" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05080e]/85 via-transparent to-[#05080e]/85 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            {/* Left: Emblem and Executive Presentation */}
            <div className="lg:col-span-6 flex flex-col items-start text-left">
              <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-3">
                {t('nav_about', 'ADVOKATLAR HAQIDA')} • JIZZAX ADVOKATLARI
              </span>

              <h2 className="font-serif text-2xl sm:text-4xl font-bold text-slate-100 tracking-tight leading-tight mb-4">
                {t('middle_statement_title', 'Yuqori Tajribaga Ega. Mukammal Himoya. Ijobiy Natija.')}
              </h2>

              <p className="text-slate-300 text-xs sm:text-sm font-light leading-relaxed mb-4">
                {t('about_mission_desc_1', 'Jizzax Advokatlari — O‘zbekistonda yetakchi yuridik amaliyot jamoalaridan biridir. Biz mijozlarimizga shunchaki maslahat bermaymiz, balki ularning buzilgan huquqlarini tiklashda kafolatlovchi strategik yechimlarni taqdim etamiz.')}
              </p>

              <p className="text-slate-400 text-xs sm:text-sm font-light leading-relaxed mb-6">
                <span>{currentLang === 'uz' ? "Barcha sud va tergov bosqichlarida ishonchli yuridik himoya hamda advokatlik kafolati. " : currentLang === 'ru' ? "Надежная юридическая защита на всех стадиях следствия и суда. " : "Reliable legal defense at all trial and investigation stages. "}</span>
                <span>{currentLang === 'uz' ? "Jizzax shahar, Islom Karimov ko'chasi, Turon Bank ro'parasi. " : currentLang === 'ru' ? "г. Джизак, ул. Ислама Каримова, напротив Турон Банка. " : "Jizzakh, Islam Karimov street, opp. Turon Bank. "}</span>
                <span className="whitespace-nowrap font-medium text-slate-300">{currentLang === 'ru' ? 'Тел:' : 'Tel:'} <span className="font-mono font-bold text-slate-100">+998 (97) 325-07-70</span></span>
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center space-x-2 py-3 px-6 rounded-lg bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-[#b38038]/25 transition-all group"
                >
                  <span>{t('btn_learn_more', 'Advokatlar haqida batafsil')}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>

                <button
                  type="button"
                  onClick={() => setBookingModalOpen(true)}
                  className="inline-flex items-center justify-center space-x-2 py-3 px-6 rounded-lg bg-[#0b1322] border border-[#c59b27]/40 hover:border-[#c59b27] text-slate-200 hover:text-white text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
                >
                  <span>{t('nav_book_cta', 'Qabulga yozilish')}</span>
                </button>
              </div>
            </div>

            {/* Right: Key Credentials & Firm Badges Card */}
            <div className="lg:col-span-6">
              <div className="bg-[#0a0f19]/90 border border-[#c59b27]/30 rounded-2xl p-6 sm:p-8 shadow-2xl shadow-black/90 backdrop-blur-md">
                <div className="flex items-center justify-between mb-6 pb-6 border-b border-[#162238]">
                  <BrandLogo size="medium" showSubtitle={true} />
                </div>

                {/* 4 Stat Cards — Upright Smooth Animations without tilting/crookedness */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#070b13] border border-[#162238] hover:border-[#c59b27]/80 rounded-xl p-4.5 shadow-sm hover:shadow-xl hover:shadow-[#c59b27]/12 hover:-translate-y-1.5 transition-all duration-300 group cursor-default relative overflow-hidden">
                    <div className="absolute -top-8 -right-8 w-24 h-24 bg-[#c59b27]/5 rounded-full blur-xl pointer-events-none group-hover:bg-[#c59b27]/15 transition-all duration-500" />
                    <div className="flex items-center text-[#c59b27] mb-1.5 group-hover:text-amber-300 transition-colors">
                      <Scale className="w-5 h-5 mr-2 group-hover:scale-120 group-hover:-translate-y-0.5 transition-all duration-300 shrink-0" />
                      <span className="font-serif text-xl sm:text-2xl font-bold text-slate-100 group-hover:text-white transition-colors">20+</span>
                    </div>
                    <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider group-hover:text-amber-200 transition-colors">
                      {currentLang === 'uz' ? '20+ yillik staj' : currentLang === 'ru' ? '20+ лет стажа' : '20+ Years Experience'}
                    </p>
                    <p className="text-[10px] text-slate-400 font-light mt-0.5">
                      {currentLang === 'uz' ? 'Yetakchi advokatlar' : currentLang === 'ru' ? 'Ведущие адвокаты' : 'Top attorneys'}
                    </p>
                  </div>

                  <div className="bg-[#070b13] border border-[#162238] hover:border-[#c59b27]/80 rounded-xl p-4.5 shadow-sm hover:shadow-xl hover:shadow-[#c59b27]/12 hover:-translate-y-1.5 transition-all duration-300 group cursor-default relative overflow-hidden">
                    <div className="absolute -top-8 -right-8 w-24 h-24 bg-[#c59b27]/5 rounded-full blur-xl pointer-events-none group-hover:bg-[#c59b27]/15 transition-all duration-500" />
                    <div className="flex items-center text-[#c59b27] mb-1.5 group-hover:text-amber-300 transition-colors">
                      <Award className="w-5 h-5 mr-2 group-hover:scale-120 group-hover:-translate-y-0.5 transition-all duration-300 shrink-0" />
                      <span className="font-serif text-xl sm:text-2xl font-bold text-slate-100 group-hover:text-white transition-colors">1,500+</span>
                    </div>
                    <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider group-hover:text-amber-200 transition-colors">
                      {currentLang === 'uz' ? 'Ko‘rib chiqilgan ishlar' : currentLang === 'ru' ? 'Рассмотренных дел' : 'Handled Cases'}
                    </p>
                    <p className="text-[10px] text-slate-400 font-light mt-0.5">
                      {currentLang === 'uz' ? 'Sud va tergov amaliyotida' : currentLang === 'ru' ? 'В судебной практике' : 'In court practice'}
                    </p>
                  </div>

                  <div className="bg-[#070b13] border border-[#162238] hover:border-[#c59b27]/80 rounded-xl p-4.5 shadow-sm hover:shadow-xl hover:shadow-[#c59b27]/12 hover:-translate-y-1.5 transition-all duration-300 group cursor-default relative overflow-hidden">
                    <div className="absolute -top-8 -right-8 w-24 h-24 bg-[#c59b27]/5 rounded-full blur-xl pointer-events-none group-hover:bg-[#c59b27]/15 transition-all duration-500" />
                    <div className="flex items-center text-[#c59b27] mb-1.5 group-hover:text-amber-300 transition-colors">
                      <Users className="w-5 h-5 mr-2 group-hover:scale-120 group-hover:-translate-y-0.5 transition-all duration-300 shrink-0" />
                      <span className="font-serif text-xl sm:text-2xl font-bold text-slate-100 group-hover:text-white transition-colors">2,500+</span>
                    </div>
                    <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider group-hover:text-amber-200 transition-colors">
                      {currentLang === 'uz' ? 'Mijozlar ishonchi' : currentLang === 'ru' ? 'Доверие клиентов' : 'Trusted Clients'}
                    </p>
                    <p className="text-[10px] text-slate-400 font-light mt-0.5">
                      {currentLang === 'uz' ? 'Muvaffaqiyatli himoya' : currentLang === 'ru' ? 'Успешная защита' : 'Protected rights'}
                    </p>
                  </div>

                  <div className="bg-[#070b13] border border-[#162238] hover:border-[#c59b27]/80 rounded-xl p-4.5 shadow-sm hover:shadow-xl hover:shadow-[#c59b27]/12 hover:-translate-y-1.5 transition-all duration-300 group cursor-default relative overflow-hidden">
                    <div className="absolute -top-8 -right-8 w-24 h-24 bg-[#c59b27]/5 rounded-full blur-xl pointer-events-none group-hover:bg-[#c59b27]/15 transition-all duration-500" />
                    <div className="flex items-center text-[#c59b27] mb-1.5 group-hover:text-amber-300 transition-colors">
                      <Award className="w-5 h-5 mr-2 group-hover:scale-120 group-hover:-translate-y-0.5 transition-all duration-300 shrink-0" />
                      <span className="font-serif text-xl sm:text-2xl font-bold text-slate-100 group-hover:text-white transition-colors">100%</span>
                    </div>
                    <p className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider group-hover:text-amber-200 transition-colors">
                      {currentLang === 'uz' ? 'Maxfiylik' : currentLang === 'ru' ? 'Конфиденциальность' : 'Confidentiality'}
                    </p>
                    <p className="text-[10px] text-slate-400 font-light mt-0.5">
                      {currentLang === 'uz' ? 'Advokatlik siri kafolati' : currentLang === 'ru' ? 'Адвокатская тайна' : 'Guaranteed'}
                    </p>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>


      {/* =========================================================================
          4. SECTION: AMALIYOTLAR (PRACTICE AREAS & LEGAL SERVICES)
      ========================================================================= */}
      <section id="services" className="relative py-20 sm:py-28 bg-[#04070d] border-b border-[#141d2d] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-2">
                {t('nav_services', 'AMALIYOTLAR')} • LEGAL PRACTICE AREAS
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-100">
                {currentLang === 'uz' ? 'Yuridik Amaliyotlar va Yo‘nalishlar' : currentLang === 'ru' ? 'Юридические практики и направления' : 'Legal Practices & Practice Areas'}
              </h2>
              <p className="text-slate-400 text-xs sm:text-sm font-light mt-2 max-w-2xl leading-relaxed">
                {currentLang === 'uz'
                  ? 'Har bir soha bo‘yicha ixtisoslashgan yetakchi advokatlarimiz eng murakkab ishlarda ijobiy natija ko‘rsatadi. Sohani tanlab, batafsil katalog bilan tanishing.'
                  : currentLang === 'ru'
                  ? 'Наши ведущие адвокаты специализируются в каждой сфере и добиваются положительных результатов в сложнейших делах.'
                  : 'Our leading attorneys specialize in every legal discipline, delivering positive outcomes in the most challenging matters.'}
              </p>
            </div>

            <Link
              to="/services"
              className="mt-4 md:mt-0 text-xs font-semibold uppercase tracking-wider text-[#c59b27] hover:text-amber-200 inline-flex items-center group cursor-pointer shrink-0"
            >
              <span>{currentLang === 'uz' ? 'Barcha amaliyotlarni ko‘rish' : currentLang === 'ru' ? 'Все практики' : 'View all practices'}</span>
              <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* LEFT: "We Fight For Justice" Interactive Topics Checklist */}
            <div className="lg:col-span-5 flex flex-col">
              <div className="bg-[#090e18] border border-[#c59b27]/30 rounded-2xl p-6 sm:p-7 shadow-xl h-full flex flex-col justify-between">
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-100 tracking-tight mb-2">
                    {t('fight_justice_title_1', 'We Fight For')} <span className="text-[#c59b27]">{t('fight_justice_title_2', 'Justice')}</span>
                  </h3>
                  <p className="text-slate-400 text-xs font-light leading-relaxed mb-5">
                    {t('fight_justice_desc', 'Our law firm is built on a foundation of trust, integrity, and a relentless commitment to achieving justice for our clients.')}
                  </p>

                  {/* Interactive Topics List */}
                  <div className="space-y-2.5">
                    {practiceTopics.map((topic) => (
                      <button
                        key={topic.id}
                        type="button"
                        onClick={() => handleOpenTopicCatalog(topic)}
                        className="w-full flex items-center justify-between p-3 rounded-lg bg-[#070b13] border border-[#162238] hover:border-[#c59b27] hover:bg-[#0f1728] transition-all text-left group cursor-pointer"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-5 h-5 rounded-full bg-[#142033] border border-[#c59b27]/60 flex items-center justify-center text-[#c59b27] shrink-0">
                            <Check className="w-3 h-3" />
                          </div>
                          <span className="text-xs sm:text-sm font-medium text-slate-200 group-hover:text-amber-200 transition-colors">
                            {currentLang === 'uz' ? topic.titleUz : currentLang === 'ru' ? (topic.titleRu || topic.titleUz) : (topic.titleEn || topic.title)}
                          </span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-[#c59b27] group-hover:translate-x-1 transition-all" />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-5 mt-4 border-t border-[#162238]">
                  <Link
                    to="/services"
                    className="w-full py-2.5 px-4 rounded border border-slate-700/80 hover:border-[#c59b27] bg-[#0c1322] text-xs font-semibold uppercase tracking-wider text-slate-200 hover:text-white flex items-center justify-center transition-all group"
                  >
                    <span>{currentLang === 'uz' ? 'Barcha sohalar bilan tanishish' : currentLang === 'ru' ? 'Ознакомиться со всеми сферами' : 'Explore All Practice Areas'}</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform text-[#c59b27]" />
                  </Link>
                </div>
              </div>
            </div>

            {/* RIGHT: Top Practice Cards Grid - Precision Equal Height Alignment */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 h-full">
              {services.slice(0, 4).map((service) => (
                <div
                  key={service.id}
                  className="bg-[#090e18] border border-[#162238] hover:border-[#c59b27]/80 rounded-xl p-5 shadow-lg hover:shadow-2xl hover:shadow-[#c59b27]/15 transition-all duration-300 flex flex-col justify-between group hover:-translate-y-2 h-full min-h-[230px] relative overflow-hidden"
                >
                  <div className="absolute -top-10 -right-10 w-28 h-28 bg-[#c59b27]/0 group-hover:bg-[#c59b27]/15 rounded-full blur-2xl transition-all duration-500 pointer-events-none" />
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-9 h-9 rounded-lg bg-[#111a2c] border border-[#c59b27]/30 flex items-center justify-center text-[#c59b27] group-hover:border-[#c59b27] group-hover:bg-[#182742] group-hover:scale-110 transition-all duration-300">
                        <Briefcase className="w-4 h-4" />
                      </div>
                      <span className="text-[10px] uppercase font-semibold text-[#c59b27] px-2 py-0.5 rounded bg-[#c59b27]/10 border border-[#c59b27]/20 group-hover:bg-[#c59b27]/20 transition-colors">
                        {currentLang === 'uz' ? 'Amaliyot' : currentLang === 'ru' ? 'Практика' : 'Practice'}
                      </span>
                    </div>

                    <h4 className="font-serif text-sm sm:text-base font-bold text-slate-100 mb-2 group-hover:text-amber-200 transition-colors line-clamp-2 min-h-[44px] flex items-center leading-snug">
                      {localized(service, 'title')}
                    </h4>
                    <p className="text-slate-400 text-xs font-light line-clamp-3 leading-relaxed mb-4 min-h-[52px]">
                      {localized(service, 'shortDesc')}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-[#162238] flex items-center justify-between mt-auto">
                    <button
                      type="button"
                      onClick={() => handleOpenTopicCatalog({
                        id: service.id,
                        titleUz: service.titleUz || service.title,
                        titleRu: service.titleRu || service.title,
                        titleEn: service.titleEn || service.title,
                        descriptionUz: service.fullDescUz || service.shortDescUz || service.shortDesc,
                        descriptionRu: service.fullDescRu || service.shortDescRu || service.shortDesc,
                        descriptionEn: service.fullDescEn || service.shortDescEn || service.shortDesc,
                        items: [
                          `${localized(service, 'title')} bo‘yicha to‘liq sud himoyasi va vakillik`,
                          'Hujjatlarni chuqur huquqiy ekspertizadan o‘tkazish',
                          'Muzokaralar olib borish va nizolarni tinch yo‘l bilan hal qilish',
                          'Apellyatsiya va kassatsiya instansiyalarida ishlarni himoya qilish'
                        ]
                      })}
                      className="text-[11px] font-semibold text-[#c59b27] hover:text-amber-200 flex items-center cursor-pointer group/btn transition-colors"
                    >
                      <span>{currentLang === 'uz' ? 'Katalog' : currentLang === 'ru' ? 'Каталог' : 'Catalog'}</span>
                      <ChevronRight className="w-3.5 h-3.5 ml-0.5 group-hover/btn:translate-x-1 transition-transform" />
                    </button>

                    <button
                      type="button"
                      onClick={() => setBookingModalOpen(true)}
                      className="text-[11px] font-semibold text-slate-300 hover:text-white cursor-pointer"
                    >
                      {t('nav_book_cta', 'Qabulga yozilish')} →
                    </button>
                  </div>
                </div>
              ))}
            </div>

          </div>

        </div>
      </section>


      {/* =========================================================================
          4. BOTTOM SECTION: INTEGRATED 3-PANEL BOOKING COMPONENT
      ========================================================================= */}
      <section id="booking" className="py-20 lg:py-28 bg-[#04070d] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-2">
              {t('online_booking_badge', 'ONLAYN BRON QILISH')}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-100">
              {t('online_booking_title', 'Qabulga Yozilish Tizimi')}
            </h2>
            <p className="text-slate-400 text-xs sm:text-sm font-light mt-3">
              {t('online_booking_desc', 'O‘zingizga qulay sana va vaqtni tanlang. Ma’muriyat arizangizni zudlik bilan ko‘rib chiqadi va rasmiy tasdiqnoma taqdim etadi.')}
            </p>
          </div>

          {/* Integrated 3-Panel Booking Card */}
          <IntegratedBookingSection />

        </div>
      </section>


      {/* =========================================================================
          5. ATTORNEYS DIRECTORY SHOWCASE
      ========================================================================= */}
      {lawyers.length > 0 && (
        <section id="lawyers" ref={lawyersSectionRef} className="py-20 bg-[#060a12] border-t border-[#141d2d] overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
              <div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-2">
                  {t('sec_lawyers_badge', 'MUTAXASSISLAR')}
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-100">
                  {t('sec_lawyers_title', 'Yetakchi Advokatlar Jamoasi')}
                </h2>
              </div>
              <Link
                to="/lawyers"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="mt-4 md:mt-0 text-xs font-semibold uppercase tracking-wider text-[#c59b27] hover:text-amber-200 inline-flex items-center group cursor-pointer"
              >
                <span>{t('view_all_lawyers', 'Barcha advokatlarni ko‘rish')}</span>
                <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {lawyers.map((lawyer, index) => (
                <div
                  key={lawyer.id}
                  style={{ animationDelay: `${index * 150}ms` }}
                  className={`${
                    lawyersVisible ? 'animate-lawyer-slide-in' : ''
                  } bg-[#090e18] rounded-xl border border-[#1b2b46] overflow-hidden group hover:border-[#c59b27]/60 hover:-translate-y-1.5 transition-all duration-300 shadow-xl hover:shadow-2xl cursor-pointer will-change-transform`}
                  onClick={() => setSelectedLawyerForDetail(lawyer)}
                >
                  <div className="relative h-72 overflow-hidden bg-[#0c1424]">
                    <img
                      src={lawyer.photoUrl || "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=600"}
                      alt={lawyer.fullName}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#090e18] via-transparent to-transparent" />
                  </div>
                  
                  <div className="p-5">
                    <h3 className="font-serif text-lg font-bold text-slate-100 group-hover:text-amber-200 transition-colors">
                      {lawyer.fullName}
                    </h3>
                    <p className="text-xs text-[#c59b27] font-medium mt-0.5">
                      {localizeLawyerPosition(lawyer.position || localized(lawyer, 'position') || 'Advokat', currentLang)}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-2 font-light">
                      {lawyer.experienceYears} {t('lawyer_experience_suffix', 'yillik tajriba')}
                    </p>

                    <div className="mt-4 pt-4 border-t border-[#162238] flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => handleBookLawyer(lawyer)}
                        className="text-[11px] text-[#c59b27] font-semibold hover:underline cursor-pointer"
                      >
                        {t('nav_book_cta', 'Qabulga yozilish')}
                      </button>

                      {/* Lawyer Rating Star Button (Professional Sharp 5-Pointed Star SVG) */}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedLawyerForReview(lawyer);
                          setReviewModalOpen(true);
                        }}
                        className="w-6 h-6 rounded-full border border-[#c59b27]/80 hover:border-[#d4af37] bg-gradient-to-b from-[#0e1728] to-[#070c16] hover:from-[#14223a] hover:to-[#0a1220] flex items-center justify-center shadow-sm shadow-black/50 transition-all cursor-pointer group active:scale-95"
                        title={currentLang === 'uz' ? `${lawyer.fullName} faoliyatiga baho berish` : currentLang === 'ru' ? `Оценить работу адвоката ${lawyer.fullName}` : `Rate ${lawyer.fullName}'s legal service`}
                        aria-label={currentLang === 'uz' ? `${lawyer.fullName} faoliyatiga baho berish` : currentLang === 'ru' ? `Оценить работу адвоката ${lawyer.fullName}` : `Rate ${lawyer.fullName}'s legal service`}
                      >
                        <svg
                          className="w-3.5 h-3.5 group-hover:scale-110 transition-transform drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <defs>
                            <linearGradient id={`starGrad-home-${lawyer.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#fef08a" />
                              <stop offset="50%" stopColor="#eab308" />
                              <stop offset="100%" stopColor="#a16207" />
                            </linearGradient>
                          </defs>
                          <polygon
                            points="12,1.75 15.05,8.25 22.25,9.15 16.95,14.1 18.35,21.25 12,17.75 5.65,21.25 7.05,14.1 1.75,9.15 8.95,8.25"
                            fill={`url(#starGrad-home-${lawyer.id})`}
                            stroke="#b48316"
                            strokeWidth="0.75"
                            strokeLinejoin="miter"
                          />
                        </svg>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedChatLawyer(lawyer);
                          setChatModalOpen(true);
                        }}
                        className="text-[11px] font-semibold text-[#c59b27] hover:text-amber-300 flex items-center gap-1 cursor-pointer transition-colors"
                        title={currentLang === 'uz' ? `${lawyer.fullName} bilan shaxsiy chat` : currentLang === 'ru' ? `Личный чат с адвокатом ${lawyer.fullName}` : `Direct chat with ${lawyer.fullName}`}
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span>{currentLang === 'ru' ? 'Чат' : 'Chat'}</span>
                        {!isSubscribed && <Lock className="w-2.5 h-2.5 text-amber-400" />}
                      </button>

                      <button
                        type="button"
                        onClick={() => setSelectedLawyerForDetail(lawyer)}
                        className="text-[11px] text-slate-400 hover:text-white cursor-pointer"
                      >
                        {t('lawyer_detail_btn', 'Batafsil →')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* =========================================================================
          6. OFISLAR VA GOOGLE MAPS
      ========================================================================= */}
      {offices.length > 0 && (
        <section id="offices" className="py-20 bg-[#05080e] border-t border-[#141d2d] relative">
          <div id="contact" className="absolute -top-10" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
              <div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-2">
                  {t('sec_offices_badge', 'JOYLASHUV')}
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-100">
                  {t('sec_offices_title', 'Ofislarimiz va Filiallar')}
                </h2>
              </div>

              {/* Office city tabs */}
              <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
                {offices.map((off) => (
                  <button
                    key={off.id}
                    onClick={() => setSelectedOffice(off)}
                    className={`px-3.5 py-1.5 sm:px-4 sm:py-2 rounded text-xs font-semibold tracking-wider transition-all cursor-pointer ${
                      selectedOffice?.id === off.id
                        ? 'bg-[#c59b27] text-[#070c16] font-bold shadow-md shadow-[#c59b27]/30'
                        : 'bg-[#0f1728] text-slate-300 hover:bg-[#15233b] hover:text-white'
                    }`}
                  >
                    {currentLang === 'en' && off.city === 'Jizzax' ? 'Jizzakh' : currentLang === 'ru' && off.city === 'Jizzax' ? 'Джизак' : off.city}
                  </button>
                ))}
              </div>
            </div>

            {selectedOffice && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
                <div className="lg:col-span-7 xl:col-span-8 flex flex-col">
                  <GoogleMapPreview 
                    office={selectedOffice} 
                    className="flex-1 w-full h-full min-h-[460px] sm:min-h-[490px]" 
                  />
                </div>

                <div 
                  key={selectedOffice.id}
                  className="lg:col-span-5 xl:col-span-4 bg-[#0a0f19]/95 border border-[#1b2b46] rounded-2xl p-6 sm:p-7 flex flex-col justify-between relative overflow-hidden animate-office-card animate-office-glow hover:border-[#c59b27]/80 hover:shadow-2xl hover:shadow-[#c59b27]/20 transition-all duration-500 group backdrop-blur-md"
                >
                  {/* Animated Gold Header Highlight */}
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#c59b27] to-transparent opacity-85 animate-pulse pointer-events-none" />
                  <div className="absolute -top-16 -right-16 w-36 h-36 bg-[#c59b27]/10 rounded-full blur-2xl pointer-events-none group-hover:bg-[#c59b27]/25 transition-all duration-700" />

                  <div className="flex items-start space-x-3 pb-3 border-b border-[#1b2b46]/80">
                    <div className="p-2 rounded-lg bg-[#c59b27]/10 border border-[#c59b27]/25 text-[#c59b27] shrink-0 mt-0.5 group-hover:scale-110 group-hover:bg-[#c59b27]/20 transition-all duration-300">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-wider text-[#c59b27] font-semibold block mb-0.5">
                        {t('firm_name_badge', 'JIZZAX ADVOKATLARI')}
                      </span>
                      <h3 className="font-serif font-bold text-base sm:text-lg text-slate-100 group-hover:text-white transition-colors">
                        {localizeOfficeName(selectedOffice.name, currentLang)}
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Clickable Address that opens location on Google Maps */}
                    <a
                      href={
                        selectedOffice.mapUrl ||
                        selectedOffice.googleMapsUrl ||
                        `https://www.google.com/maps/search/?api=1&query=${selectedOffice.latitude ?? 40.160331},${selectedOffice.longitude ?? 67.827246}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Google Maps xaritasida ofis joylashuvini ochish"
                      className="block p-3 rounded-xl bg-[#070b13]/80 border border-[#141f33] hover:border-[#c59b27] hover:bg-[#0e172a] hover:shadow-md hover:shadow-[#c59b27]/20 transition-all duration-300 group/item cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase text-slate-400 group-hover/item:text-[#c59b27] font-semibold flex items-center transition-colors">
                          <MapPin className="w-3 h-3 mr-1.5 shrink-0 text-[#c59b27]" />
                          {t('office_address_label', 'Manzil')}
                        </span>
                        <span className="text-[10px] text-[#c59b27] flex items-center gap-1 opacity-80 group-hover/item:opacity-100 font-medium">
                          <span>{currentLang === 'uz' ? 'Xaritada ochish' : currentLang === 'ru' ? 'На карте' : 'Open Map'}</span>
                          <ExternalLink className="w-3 h-3 group-hover/item:translate-x-0.5 transition-transform" />
                        </span>
                      </div>
                      <p className="text-slate-300 text-xs leading-relaxed group-hover/item:text-white transition-colors">
                        {localized(selectedOffice, 'address') || selectedOffice.addressUz || selectedOffice.address}
                      </p>
                    </a>

                    <div className="p-3 rounded-xl bg-[#070b13]/80 border border-[#141f33] hover:border-[#c59b27]/40 hover:bg-[#0c1424] transition-all duration-300 group/item">
                      <span className="text-[10px] uppercase text-slate-500 group-hover/item:text-[#c59b27] font-semibold flex items-center mb-1 transition-colors">
                        <Phone className="w-3 h-3 mr-1.5 shrink-0" />
                        {t('office_phone_label', 'Telefon')}
                      </span>
                      <a 
                        href={`tel:${selectedOffice.phone?.replace(/[^+\d]/g, '') || '+998973250770'}`}
                        className="text-slate-200 text-xs font-mono font-medium hover:text-[#c59b27] transition-colors inline-block"
                      >
                        {selectedOffice.phone || '+998 (97) 325-07-70'}
                      </a>
                    </div>

                    <div className="p-3 rounded-xl bg-[#070b13]/80 border border-[#141f33] hover:border-[#c59b27]/40 hover:bg-[#0c1424] transition-all duration-300 group/item">
                      <span className="text-[10px] uppercase text-slate-500 group-hover/item:text-[#c59b27] font-semibold flex items-center mb-1 transition-colors">
                        <Clock className="w-3 h-3 mr-1.5 shrink-0" />
                        {t('office_hours_label', 'Ish Tartibi')}
                      </span>
                      <p className="text-slate-300 text-xs group-hover/item:text-slate-100 transition-colors">
                        {localizeWorkingHours(selectedOffice.workingHoursText || 'Dush - Shanba: 09:00 - 18:00', currentLang)}
                      </p>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      onClick={() => setBookingModalOpen(true)}
                      className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#b38038] via-[#c59b27] to-[#b38038] bg-[length:200%_auto] hover:bg-[position:right_center] hover:scale-[1.02] active:scale-[0.98] text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-[#b38038]/25 hover:shadow-xl hover:shadow-[#c59b27]/35 transition-all duration-300 cursor-pointer flex items-center justify-center space-x-2 group/btn"
                    >
                      <span>{t('office_book_btn', 'Ushbu ofisda qabulga yozilish')}</span>
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* =========================================================================
          PLATFORM GUIDE & HELP SECTION (Right Above Footer)
      ========================================================================= */}
      <PlatformGuideSection onOpenPayment={() => setSubscriptionModalOpen(true)} />

      {/* =========================================================================
          MODALS
      ========================================================================= */}
      
      {/* 1. Practice Area Catalog Modal */}
      <PracticeAreaCatalogModal
        isOpen={catalogModalOpen}
        onClose={() => setCatalogModalOpen(false)}
        topic={selectedTopic}
        onBookTopic={handleBookFromCatalog}
        onBookLawyer={(l) => {
          setCatalogModalOpen(false);
          handleBookLawyer(l);
        }}
        lawyers={lawyers}
      />

      {/* 2. Lawyer Full Dossier Modal ("Batafsil") */}
      <LawyerDetailModal
        isOpen={!!selectedLawyerForDetail}
        onClose={() => setSelectedLawyerForDetail(null)}
        lawyer={selectedLawyerForDetail}
        onBookAppointment={(l) => {
          setSelectedLawyerForDetail(null);
          handleBookLawyer(l);
        }}
        onChatLawyer={(l) => {
          setSelectedLawyerForDetail(null);
          setSelectedChatLawyer(l);
          setChatModalOpen(true);
        }}
        onOpenSubscription={() => {
          setSelectedLawyerForDetail(null);
          setSubscriptionModalOpen(true);
        }}
      />

      {/* 3. Instant Booking Modal (No jumping down) */}
      <InstantBookingModal
        isOpen={bookingModalOpen}
        onClose={() => {
          setBookingModalOpen(false);
          setSelectedLawyerForBooking(null);
        }}
        preSelectedTopic={selectedTopic}
        initialLawyerId={selectedLawyerForBooking?.id}
      />

      {/* 4. Lawyer Rating & Review Modal ("i" button) */}
      <ReviewSubmissionModal
        isOpen={reviewModalOpen}
        onClose={() => {
          setReviewModalOpen(false);
          setSelectedLawyerForReview(null);
        }}
        lawyers={lawyers}
        initialLawyerId={selectedLawyerForReview?.id}
      />

      {/* 5. Client 1-on-1 Chat Modal */}
      <ClientChatModal
        isOpen={chatModalOpen}
        onClose={() => {
          setChatModalOpen(false);
          setSelectedChatLawyer(null);
        }}
        initialLawyer={selectedChatLawyer}
        onOpenSubscription={() => setSubscriptionModalOpen(true)}
      />

      {/* 6. Subscription & Payment Modal */}
      <SubscriptionModal
        isOpen={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        initialType="CLIENT"
        onSuccess={() => {
          if (selectedChatLawyer) {
            setChatModalOpen(true);
          }
        }}
      />

    </div>
  );
}
